/* eslint-disable react/no-deprecated */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import _ from 'underscore';
import {
  Button,
  Alert,
  FormGroup,
  FormControl,
  ControlLabel,
  Modal,
} from 'react-bootstrap';
import validator from 'email-validator';
import BillOfLadingStore from 'stores/BillOfLadingStore';
import DocumentPreview from './stops/DocumentPreview';
import { getSignedUrl, getDocumentsV2 } from '../../../utils/DocumentsAPIUtils';
import { DOCUMENT_STATUSES } from '../documents/Document';
import { showToast } from '../../../stores/ToastStore';

class SendBOLEmailModal extends React.Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onSend: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
  };

  refetchTimeoutRef = React.createRef(0);

  constructor(props) {
    super(props);
    this.state = {
      ...this.getInitialState(),
      bolUrl: null,
    };
  }

  getInitialState() {
    return {
      error: null,
      isLoading: false,
      to: '',
    };
  }

  async getBolDoc(docKey) {
    if (!docKey) return;

    const { t } = this.props;
    const { data, errors: documentErrors } = await getDocumentsV2([docKey], {
      types: 'bill_of_lading',
      options: {
        limit: 1,
        order: {
          field: 'createdAt',
          direction: 'desc',
        },
      },
    });

    if (documentErrors) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', {
          error: documentErrors[0].message,
        }),
        'error',
      );
      return;
    }

    return data.getDocumentsV2?.documents[0];
  }

  async getDocUrl(docKey) {
    if (!docKey) return;

    const { t } = this.props;
    const { data, errors: signedUrlErrors } = await getSignedUrl(docKey);

    if (signedUrlErrors) {
      showToast(
        t('GET_SIGNED_URL_ERROR'),
        t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
          error: signedUrlErrors[0].message,
        }),
        'error',
      );
      return;
    }

    return data?.getSignedUrl?.signedUrl;
  }

  handleBOLChange = async () => {
    const bol_response = BillOfLadingStore.getBillOfLading();
    this.refreshView();
    let bolStatus = bol_response.bol.status;

    if (bol_response.error) return;
    this.refetchTimeoutRef.current = setInterval(async () => {
      if (bolStatus !== DOCUMENT_STATUSES.PENDING) {
        clearTimeout(this.refetchTimeoutRef.current);
        const signedUrl = await this.getDocUrl(bol_response.bol?.key);

        if (!signedUrl) return;

        this.setState({
          bolUrl: signedUrl,
          isLoading: false,
        });
      } else {
        const doc = await this.getBolDoc(bol_response.bol.foreignKeys[0]);
        if (!doc) return;

        bolStatus = doc.status;
      }
    }, 2000);
  };

  componentWillReceiveProps(nextProps) {
    if (!_.isEqual(this.props.shipment, nextProps.shipment)) {
      this.setState((prevState) => {
        return {
          ...this.getInitialState(),
          to: prevState.to,
        };
      });
    }
  }

  componentDidMount() {
    BillOfLadingStore.addChangeListener(this.handleBOLChange);
  }

  async componentDidUpdate(prevProps) {
    if (
      this.props.show &&
      !this.state.bolUrl &&
      prevProps.show !== this.props.show
    ) {
      const doc = await this.getBolDoc(this.props.shipment._id);
      const bolUrl = await this.getDocUrl(doc?.key);

      if (bolUrl) {
        this.setState({ bolUrl });
      }
    }
  }

  componentWillUnmount() {
    this.setState(this.getInitialState()); // ???
    BillOfLadingStore.removeChangeListener(this.handleBOLChange);
  }

  addRecipients(e) {
    this.setState({ to: e.target.value.trim() });
  }
  refreshView(toHide) {
    this.setState(this.getInitialState());
    if (toHide) this.props.onHide();
  }
  send(toSend) {
    const bol_contacts = this.retrieveBillOfLadingContacts();
    var emails = this.state.to.replace(/' '/g, '').split(',');
    emails = bol_contacts.concat(emails);
    const filtered = emails.filter(Boolean);
    const invalidEmailAddresses = filtered.filter(
      (ea) => !validator.validate(ea),
    );
    if (invalidEmailAddresses.length > 0) {
      this.setState({ error: 'VALID_EMAIL_ONLY' });
    } else {
      this.setState({
        isLoading: true,
      });
      this.props.onSend(_.uniq(filtered), toSend);
    }
  }

  renderError() {
    if (!this.state.error) {
      return '';
    }
    const { t } = this.props;
    return <Alert bsStyle="warning">{t(this.state.error)}</Alert>;
  }

  retrieveBillOfLadingContacts() {
    const notifications = this.props.shipment.shipper.notification_preferences;
    if (notifications) {
      const bol_type = _.filter(notifications, {
        type: 'bill_of_lading_contacts',
      });
      if (Array.isArray(bol_type) && bol_type.length) {
        return bol_type[0].email.recipients;
      }
    }
    return [];
  }

  render() {
    var contacts = this.retrieveBillOfLadingContacts();
    contacts = contacts.filter(Boolean);
    var isEnabled = false;
    if ((Array.isArray(contacts) && contacts.length) || this.state.to !== '') {
      isEnabled = true;
    }
    const { t } = this.props;
    const buttonTitle = this.state.isLoading ? 'LOADING' : 'GENERATE';
    return (
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        enforceFocus={false}
      >
        <Modal.Header>
          <strong>{t('BILL_OF_LADING')}</strong>
        </Modal.Header>
        <Modal.Body>
          {this.renderError()}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {this.state.bolUrl && (
              <DocumentPreview
                displayInGridView
                width="400px"
                height="565px"
                type={this.state.bolUrl}
                url={this.state.bolUrl}
                renderDownloadableImage
              />
            )}
          </div>
          <div>
            <FormGroup>
              <ControlLabel>{t('RECIPIENT', { count: 1 })}</ControlLabel>
              <FormControl
                placeholder={t('COMMA_SEPARATED_EMAILS')}
                onChange={(e) => this.addRecipients(e)}
                type="text"
                value={this.state.to}
              />
            </FormGroup>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle="danger" type="button" onClick={this.props.onHide}>
            {titleize(t('CLOSE'))}
          </Button>
          <Button
            disabled={this.state.isLoading}
            bsStyle="primary"
            type="button"
            onClick={() => this.send(false)}
          >
            {titleize(t(buttonTitle))}
          </Button>
          <Button
            disabled={!isEnabled || this.state.isLoading}
            bsStyle="primary"
            type="button"
            onClick={() => this.send(true)}
          >
            {t('SEND')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withTranslation()(SendBOLEmailModal);
